import { api } from "@/lib/api";

export const REQUEST_TIMEOUT_MS = 15000;

const isTimeout = (err) => err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "");

// A failed or timed-out admin request goes to the console and the server log, so a stuck card is traceable.
export const reportFailure = (where, err, startedAt) => {
  const cfg = err?.config || {};
  const status = err?.response?.status ?? null;
  if (status === 401 || status === 403) return; // signed out / not allowed — the page redirects
  console.error(`[${where}] request failed`, cfg.url, status ?? err?.message);
  api.post("/client-log", {
    where: String(where || "").slice(0, 80),
    method: String(cfg.method || "get").toUpperCase().slice(0, 10),
    url: String(cfg.url || "").slice(0, 400),
    status,
    elapsed_ms: startedAt ? Date.now() - startedAt : null,
    message: (isTimeout(err) ? `timed out after ${REQUEST_TIMEOUT_MS / 1000}s` : err?.message || "").slice(0, 300),
  }, { timeout: REQUEST_TIMEOUT_MS }).catch(() => {});
};

// GET with a hard timeout; failures are reported, then re-thrown for the caller's Retry state.
// The same URL asked for twice while the first is still on its way shares that one request.
const inflight = new Map();
const early = new Map(); // url → {req, at}: started before the page code arrived, handed to the first asker
export const timedGet = (url, where, config = {}) => {
  const shareable = !config.params && !config.signal;
  if (shareable && early.has(url)) {
    const e = early.get(url);
    early.delete(url);
    if (Date.now() - e.at < 10000) return e.req;
  }
  if (shareable && inflight.has(url)) return inflight.get(url);
  const t0 = Date.now();
  const req = api.get(url, { timeout: REQUEST_TIMEOUT_MS, ...config }).catch((err) => {
    reportFailure(where || url, err, t0);
    throw err;
  }).finally(() => { if (inflight.get(url) === req) inflight.delete(url); });
  if (shareable) inflight.set(url, req);
  return req;
};

// Start a GET now (while the page's code is still downloading); the first timedGet for it reuses it.
export const prefetchGet = (url) => {
  const req = timedGet(url, `prefetch ${url}`);
  req.catch(() => early.delete(url)); // a failed head start (e.g. signed out) is never handed out later
  early.set(url, { req, at: Date.now() });
};
